import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { config } from "@noted/configuration";
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";

export type Context = {
  id: number;
  name: string;
  email?: string;
  isSuspended: boolean;
  roles: string[];
  organisation?: {
    id: number;
    name: string;
  };
  session?: string;
};

function isUserRole(userRole: "WORKER" | "ORG-ADMIN" | "NOTED-ADMIN") {
  return (context: Context) => context.roles.includes(userRole);
}

export const isDarkNotedUser = isUserRole("NOTED-ADMIN");
export const isOrgAdmin = isUserRole("ORG-ADMIN");

export function useGetContextsQuery() {
  const { getAccessToken } = useKindeAuth();

  return useSuspenseQuery<Context[]>({
    queryKey: ["contexts"],
    queryFn: async () => {
      const token = await getAccessToken();

      const response = await fetch(`${config.apiUrl}/api/v1/contexts`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return handleResponse(response);
    },
  });
}

export type UseCreateContextSessionMutationError = {
  message?: string;
  code?: string;
};

export function useCreateContextSessionMutation() {
  const queryClient = useQueryClient();
  const { getAccessToken } = useKindeAuth();

  return useMutation<string, UseCreateContextSessionMutationError, number>({
    mutationFn: async contextId => {
      const token = await getAccessToken();

      return createSession(token, contextId);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["contexts"] }),
  });
}

export async function createSession(token = "", contextId: number) {
  const response = await fetch(`${config.apiUrl}/api/v1/sessions`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "content-type": "application/json",
    },
    body: JSON.stringify({ contextId }),
  });

  const session = (await handleResponse(response)) as { id: string };
  return session.id;
}

async function handleResponse(res: Response) {
  if (!res.ok) {
    let error: Record<string, unknown> = { status: res.status };
    try {
      const json = await res.json();
      error = { ...error, ...json };
    } catch {
      // no-op
    }
    throw error;
  }

  if (res.status === 204) return;
  return await res.json();
}
